import { Platform } from 'react-native';
import { launchCamera, launchImageLibrary } from 'react-native-image-picker';

export type PickedPhoto = {
  uri: string;
  type: string;
  name: string;
  /** web only: the real File, needed for multipart upload */
  file?: any;
};

/**
 * Take (back camera) or choose photos on native and web.
 * react-native-image-picker's launchCamera does not work in a browser, so web uses a file input
 * (capture="environment" opens the back camera on phones, a file chooser on desktops).
 * Resolves to [] when the user cancels; rejects with an Error on a picker failure.
 */
export const pickPhotos = (source: 'camera' | 'library'): Promise<PickedPhoto[]> => {
  if (Platform.OS === 'web') {
    return new Promise(resolve => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      if (source === 'camera') {
        input.setAttribute('capture', 'environment');
      } else {
        input.multiple = true;
      }
      input.onchange = () => {
        const files: any[] = Array.from(input.files || []);
        resolve(
          files.map(f => ({
            uri: URL.createObjectURL(f),
            type: f.type || 'image/jpeg',
            name: f.name || `photo_${Date.now()}.jpg`,
            file: f
          }))
        );
      };
      input.addEventListener('cancel', () => resolve([]));
      input.click();
    });
  }

  return new Promise((resolve, reject) => {
    const done = (res: any) => {
      if (res.didCancel) return resolve([]);
      if (res.errorCode) return reject(new Error(res.errorMessage || 'Could not open the camera or gallery'));
      resolve(
        (res.assets || [])
          .filter((a: any) => a.uri)
          .map((a: any) => ({
            uri: a.uri,
            type: a.type || 'image/jpeg',
            name: a.fileName || `photo_${Date.now()}.jpg`
          }))
      );
    };
    if (source === 'camera') {
      launchCamera({ mediaType: 'photo', cameraType: 'back', quality: 0.8, saveToPhotos: false }, done);
    } else {
      launchImageLibrary({ mediaType: 'photo', quality: 0.8, selectionLimit: 5 }, done);
    }
  });
};

/** The value to append to FormData as `file`: a real File on web, the {uri,type,name} descriptor on native. */
export const toFormFile = (p: PickedPhoto): any =>
  Platform.OS === 'web' && p.file ? p.file : { uri: p.uri, type: p.type, name: p.name };
